import type { FeatureCollection, LineString } from 'geojson';
import { Layer, Source } from 'react-map-gl/mapbox';
import useMap from '../../hooks/useMap';
import useMapStyleChange from '../../hooks/useMapStyleChange';
import { getPolylineHeatmapCoreId, getPolylineHeatmapGlowId, getPolylineHeatmapSourceId, polylinesBottomId } from '../../utils/layers';

export type ZoomScales = {
  z0: number;
  z3: number;
  z6: number;
  z12: number;
  z16: number;
};

export type HeatmapLayerStyle = {
  glowColor: string;
  glowWidth: number;
  glowOpacity: number;
  glowBlur: number;
  coreColor: string;
  coreWidth: number;
  coreOpacity: number;
  coreBlur: number;
  glowWidthZoom: ZoomScales;
  glowBlurZoom: ZoomScales;
  glowOpacityZoom: ZoomScales;
  coreWidthZoom: ZoomScales;
  coreBlurZoom: ZoomScales;
  coreOpacityZoom: ZoomScales;
  tolerance: number;
  buffer: number;
};

export const DEFAULT_HEATMAP_STYLE: HeatmapLayerStyle = {
  glowColor: 'rgb(48, 229, 253)',
  glowWidth: 10,
  glowOpacity: 0.8,
  glowBlur: 12,
  coreColor: 'rgb(12, 82, 126)',
  coreWidth: 6,
  coreOpacity: 0.8,
  coreBlur: 6,
  glowWidthZoom: { z0: 4, z3: 4, z6: 3, z12: 2, z16: 2.5 },
  glowBlurZoom: { z0: 4, z3: 4, z6: 3, z12: 2, z16: 1 },
  glowOpacityZoom: { z0: 1, z3: 1, z6: 1, z12: 1, z16: 1 },
  coreWidthZoom: { z0: 4, z3: 4, z6: 3, z12: 2, z16: 1.5 },
  coreBlurZoom: { z0: 1, z3: 4, z6: 3, z12: 2, z16: 1 },
  coreOpacityZoom: { z0: 1, z3: 1, z6: 1, z12: 1, z16: 4 },
  tolerance: 0.4,
  buffer: 272
};

export const SATELLITE_HEATMAP_STYLE: HeatmapLayerStyle = {
  glowColor: 'rgb(12, 82, 126)',
  glowWidth: 10,
  glowOpacity: 0.9,
  glowBlur: 14,
  coreColor: 'rgb(48, 229, 253)',
  coreWidth: 6,
  coreOpacity: 0.9,
  coreBlur: 8,
  glowWidthZoom: { z0: 4, z3: 4, z6: 3, z12: 3, z16: 2.5 },
  glowBlurZoom: { z0: 4, z3: 4, z6: 3, z12: 2, z16: 1 },
  glowOpacityZoom: { z0: 1, z3: 1, z6: 1, z12: 4, z16: 4 },
  coreWidthZoom: { z0: 4, z3: 4, z6: 3, z12: 3, z16: 2 },
  coreBlurZoom: { z0: 1, z3: 4, z6: 3, z12: 2, z16: 1 },
  coreOpacityZoom: { z0: 1, z3: 1, z6: 1, z12: 4, z16: 4 },
  tolerance: 0.4,
  buffer: 272
};

type Props = {
  data: FeatureCollection<LineString>;
  style?: HeatmapLayerStyle;
  uniqueId?: string;
};

/**
 * PolylineHeatmapLayer renders a user's stacked polylines as a glow + core
 * heatmap effect, anchored below all other polylines in the polyline group.
 *
 * Two layers are stacked: a wide, blurred `glow` underneath a narrower, sharper
 * `core`. Both use the same source; widths/opacities/blurs interpolate by zoom.
 *
 * Pure presentation — the data source is the caller's problem (see PersonalHeatmap).
 */
const PolylineHeatmapLayer = ({ data, style = DEFAULT_HEATMAP_STYLE, uniqueId }: Props) => {
  const map = useMap();
  // Forces a re-render on basemap style changes so layer ordering survives style switches (DISCO-17246)
  useMapStyleChange(map);

  const sourceId = getPolylineHeatmapSourceId(uniqueId);
  const glowLayerId = getPolylineHeatmapGlowId(uniqueId);
  const coreLayerId = getPolylineHeatmapCoreId(uniqueId);

  if (!map || data.features.length === 0) {
    return null;
  }

  return (
    // tolerance/buffer aren't reactive on a Mapbox geojson source — remount the source when they change
    <Source key={`${style.tolerance}-${style.buffer}`} id={sourceId} type="geojson" data={data} tolerance={style.tolerance} buffer={style.buffer}>
      <Layer
        id={glowLayerId}
        beforeId={polylinesBottomId}
        source={sourceId}
        type="line"
        paint={{
          'line-color': style.glowColor,
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            0,
            style.glowWidth * style.glowWidthZoom.z0,
            3,
            style.glowWidth * style.glowWidthZoom.z3,
            6,
            style.glowWidth * style.glowWidthZoom.z6,
            12,
            style.glowWidth * style.glowWidthZoom.z12,
            16,
            style.glowWidth * style.glowWidthZoom.z16
          ],
          'line-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            0,
            Math.min(style.glowOpacity * style.glowOpacityZoom.z0, 1),
            3,
            Math.min(style.glowOpacity * style.glowOpacityZoom.z3, 1),
            6,
            Math.min(style.glowOpacity * style.glowOpacityZoom.z6, 1),
            12,
            Math.min(style.glowOpacity * style.glowOpacityZoom.z12, 1),
            16,
            Math.min(style.glowOpacity * style.glowOpacityZoom.z16, 1)
          ],
          'line-blur': [
            'interpolate',
            ['linear'],
            ['zoom'],
            0,
            style.glowBlur * style.glowBlurZoom.z0,
            3,
            style.glowBlur * style.glowBlurZoom.z3,
            6,
            style.glowBlur * style.glowBlurZoom.z6,
            12,
            style.glowBlur * style.glowBlurZoom.z12,
            16,
            style.glowBlur * style.glowBlurZoom.z16
          ]
        }}
        layout={{
          'line-cap': 'round',
          'line-join': 'round'
        }}
      />
      <Layer
        id={coreLayerId}
        beforeId={polylinesBottomId}
        source={sourceId}
        type="line"
        paint={{
          'line-color': style.coreColor,
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            0,
            style.coreWidth * style.coreWidthZoom.z0,
            3,
            style.coreWidth * style.coreWidthZoom.z3,
            6,
            style.coreWidth * style.coreWidthZoom.z6,
            12,
            style.coreWidth * style.coreWidthZoom.z12,
            16,
            style.coreWidth * style.coreWidthZoom.z16
          ],
          'line-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            0,
            Math.min(style.coreOpacity * style.coreOpacityZoom.z0, 1),
            3,
            Math.min(style.coreOpacity * style.coreOpacityZoom.z3, 1),
            6,
            Math.min(style.coreOpacity * style.coreOpacityZoom.z6, 1),
            12,
            Math.min(style.coreOpacity * style.coreOpacityZoom.z12, 1),
            16,
            Math.min(style.coreOpacity * style.coreOpacityZoom.z16, 1)
          ],
          'line-blur': [
            'interpolate',
            ['linear'],
            ['zoom'],
            0,
            style.coreBlur * style.coreBlurZoom.z0,
            3,
            style.coreBlur * style.coreBlurZoom.z3,
            6,
            style.coreBlur * style.coreBlurZoom.z6,
            12,
            style.coreBlur * style.coreBlurZoom.z12,
            16,
            style.coreBlur * style.coreBlurZoom.z16
          ]
        }}
        layout={{
          'line-cap': 'round',
          'line-join': 'round'
        }}
      />
    </Source>
  );
};

export default PolylineHeatmapLayer;
